import React, { useState, useRef, useEffect } from "react";
import {
  calculateBondRepayment,
  BondInputs,
  LumpSumPayment,
  InterestRateChange,
} from "../utils/bondCalculator";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useAuth } from "./AuthProvider";
import {
  getUser,
  patchUser,
  createCalculation,
  isApiConfigured,
} from "../utils/api";
import "./BondRepayment.css";

export type SavedBond = {
  bondAmount?: string;
  deposit?: string;
  interestRate?: string;
  termYears?: string;
  additionalMonthlyPayment?: string;
  lumpSumPayments?: LumpSumPayment[];
  interestRateChanges?: InterestRateChange[];
};

interface BondRepaymentProps {
  onOpenAuthForSave?: (callback: () => void) => void;
  onOpenAuth?: () => void;
  pendingRestore?: Record<string, unknown>;
  onClearPendingRestore?: () => void;
}

const BondRepayment: React.FC<BondRepaymentProps> = ({
  onOpenAuthForSave,
  onOpenAuth,
  pendingRestore,
  onClearPendingRestore,
}) => {
  const { user } = useAuth();
  const [bondAmount, setBondAmount] = useState<string>("");
  const [deposit, setDeposit] = useState<string>("");
  const [interestRate, setInterestRate] = useState<string>("10.25");
  const [termYears, setTermYears] = useState<string>("20");
  const [additionalMonthlyPayment, setAdditionalMonthlyPayment] =
    useState<string>("0");
  const [lumpSumPayments, setLumpSumPayments] = useState<LumpSumPayment[]>([
    { amount: 0, month: 0 },
  ]);
  const [interestRateChanges, setInterestRateChanges] = useState<
    InterestRateChange[]
  >([{ month: 0, rate: 0 }]);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [loadedOnce, setLoadedOnce] = useState(false);

  const reportRef = useRef<HTMLDivElement>(null);

  const canSaveAsUser = Boolean(user && isApiConfigured());

  const handleSaveClick = () => {
    if (canSaveAsUser) {
      handleSave();
    } else if (!user) {
      if (onOpenAuthForSave) {
        onOpenAuthForSave(() => handleSave());
      } else {
        onOpenAuth?.();
      }
    }
  };

  useEffect(() => {
    if (!user || !isApiConfigured() || loadedOnce) return;
    setLoadedOnce(true);
    getUser()
      .then((data) => {
        const s = data.bond as SavedBond | undefined;
        if (!s) return;
        if (s.bondAmount != null) setBondAmount(s.bondAmount);
        if (s.deposit != null) setDeposit(s.deposit);
        if (s.interestRate != null) setInterestRate(s.interestRate);
        if (s.termYears != null) setTermYears(s.termYears);
        if (s.additionalMonthlyPayment != null)
          setAdditionalMonthlyPayment(s.additionalMonthlyPayment);
        if (s.lumpSumPayments?.length) setLumpSumPayments(s.lumpSumPayments);
        if (s.interestRateChanges?.length)
          setInterestRateChanges(s.interestRateChanges);
      })
      .catch(() => setLoadedOnce(true));
  }, [user, loadedOnce]);

  useEffect(() => {
    if (!pendingRestore || !onClearPendingRestore) return;
    const s = pendingRestore as SavedBond;
    if (s.bondAmount != null) setBondAmount(String(s.bondAmount));
    if (s.deposit != null) setDeposit(String(s.deposit));
    if (s.interestRate != null) setInterestRate(String(s.interestRate));
    if (s.termYears != null) setTermYears(String(s.termYears));
    if (s.additionalMonthlyPayment != null)
      setAdditionalMonthlyPayment(String(s.additionalMonthlyPayment));
    if (s.lumpSumPayments?.length) setLumpSumPayments(s.lumpSumPayments);
    if (s.interestRateChanges?.length)
      setInterestRateChanges(s.interestRateChanges);
    onClearPendingRestore();
  }, [pendingRestore, onClearPendingRestore]);

  const handleSave = async () => {
    if (!canSaveAsUser) return;
    setSaveStatus("saving");
    const payload = {
      bondAmount,
      deposit,
      interestRate,
      termYears,
      additionalMonthlyPayment,
      lumpSumPayments,
      interestRateChanges,
    };
    try {
      await createCalculation({
        calculationType: "bond",
        payload,
      });
      await patchUser({ bond: payload }).catch(() => {});
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }
  };

  const handleLumpSumChange = (
    index: number,
    field: keyof LumpSumPayment,
    value: string,
  ) => {
    const updated = [...lumpSumPayments];
    updated[index] = {
      ...updated[index],
      [field]:
        field === "amount" ? parseFloat(value) || 0 : parseInt(value) || 0,
    };
    setLumpSumPayments(updated);
  };

  const addLumpSumPayment = () => {
    setLumpSumPayments([...lumpSumPayments, { amount: 0, month: 0 }]);
  };

  const removeLumpSumPayment = (index: number) => {
    if (lumpSumPayments.length > 1) {
      setLumpSumPayments(lumpSumPayments.filter((_, i) => i !== index));
    }
  };

  const handleInterestRateChange = (
    index: number,
    field: keyof InterestRateChange,
    value: string,
  ) => {
    const updated = [...interestRateChanges];
    updated[index] = {
      ...updated[index],
      [field]: field === "rate" ? parseFloat(value) || 0 : parseInt(value) || 0,
    };
    setInterestRateChanges(updated);
  };

  const addInterestRateChange = () => {
    setInterestRateChanges([...interestRateChanges, { month: 0, rate: 0 }]);
  };

  const removeInterestRateChange = (index: number) => {
    if (interestRateChanges.length > 1) {
      setInterestRateChanges(interestRateChanges.filter((_, i) => i !== index));
    }
  };

  const inputs: BondInputs = {
    bondAmount: parseFloat(bondAmount.replace(/[,\s]/g, "")) || 0,
    deposit: parseFloat(deposit.replace(/[,\s]/g, "")) || 0,
    interestRate: parseFloat(interestRate) || 0,
    termYears: parseInt(termYears) || 20,
    additionalMonthlyPayment:
      parseFloat(additionalMonthlyPayment.replace(/[,\s]/g, "")) || 0,
    lumpSumPayments: lumpSumPayments.filter(
      (lump) => lump.amount > 0 && lump.month > 0,
    ),
    interestRateChanges: interestRateChanges.filter(
      (change) => change.rate > 0 && change.month > 0,
    ),
  };

  const result = calculateBondRepayment(inputs);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  /** Short tick labels for the chart Y-axis (e.g. 1.2m, 850k) — avoids long currency strings overlapping the axis title */
  const formatChartAxisCurrency = (value: number): string => {
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (abs >= 1_000_000) {
      const m = abs / 1_000_000;
      const t = m >= 100 ? m.toFixed(0) : m >= 10 ? m.toFixed(1) : m.toFixed(2);
      return `${sign}${parseFloat(t)}m`;
    }
    if (abs >= 1_000) {
      const k = abs / 1_000;
      const t = k >= 100 ? k.toFixed(0) : k.toFixed(1);
      return `${sign}${parseFloat(t)}k`;
    }
    return `${sign}${Math.round(abs)}`;
  };

  const formatMonths = (months: number): string => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (years === 0) {
      return `${months} month${months !== 1 ? "s" : ""}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? "s" : ""}`;
    } else {
      return `${years} year${
        years !== 1 ? "s" : ""
      } and ${remainingMonths} month${remainingMonths !== 1 ? "s" : ""}`;
    }
  };

  const hasValidInputs =
    inputs.bondAmount > 0 &&
    inputs.deposit >= 0 &&
    inputs.interestRate > 0 &&
    inputs.termYears > 0 &&
    inputs.bondAmount > inputs.deposit;

  const exportToPDF = async () => {
    if (!reportRef.current) return;

    const root = reportRef.current;
    const canvas = await html2canvas(root, {
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
      onclone: (clonedDoc, clonedEl) => {
        clonedEl.querySelectorAll("ins.adsbygoogle").forEach((el) => el.remove());

        const stripColorMix = (css: string): string => {
          let out = "";
          let i = 0;
          while (i < css.length) {
            const idx = css.indexOf("color-mix(", i);
            if (idx === -1) { out += css.slice(i); break; }
            out += css.slice(i, idx);
            let depth = 0, j = idx;
            while (j < css.length) {
              if (css[j] === "(") depth++;
              else if (css[j] === ")") { if (--depth === 0) { j++; break; } }
              j++;
            }
            out += "transparent";
            i = j;
          }
          return out;
        };

        const allCss = Array.from(document.styleSheets)
          .flatMap((sheet) => {
            try {
              return Array.from(sheet.cssRules).map((r) => r.cssText);
            } catch {
              return [];
            }
          })
          .join("\n");

        clonedDoc.querySelectorAll('link[rel="stylesheet"], style').forEach((el) => el.remove());
        const styleEl = clonedDoc.createElement("style");
        styleEl.textContent = stripColorMix(allCss);
        clonedDoc.head.appendChild(styleEl);

        const liveAll = Array.from(root.querySelectorAll<HTMLElement>("*"));
        const clonedAll = Array.from(clonedEl.querySelectorAll<HTMLElement>("*"));
        const colorProps: (keyof CSSStyleDeclaration & string)[] = [
          "backgroundColor", "color",
          "borderTopColor", "borderRightColor", "borderBottomColor", "borderLeftColor",
          "boxShadow", "outlineColor",
        ];
        liveAll.forEach((el, i) => {
          const clone = clonedAll[i] as HTMLElement | undefined;
          if (!clone) return;
          const cs = window.getComputedStyle(el);
          colorProps.forEach((prop) => {
            const val = cs[prop as keyof CSSStyleDeclaration] as string;
            if (val) clone.style[prop as any] = val;
          });
        });
      },
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Add first page
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const timestamp = new Date().toISOString().split("T")[0];
    pdf.save(`bond-repayment-report-${timestamp}.pdf`);
  };

  return (
    <>
      <h2>Bond Repayment</h2>

      <div className="calculator-section">
        <div className="input-group">
          <label htmlFor="bond-amount">Bond Amount</label>
          <input
            id="bond-amount"
            type="text"
            value={bondAmount}
            onChange={(e) => setBondAmount(e.target.value)}
            placeholder="e.g., 2,000,000"
          />
        </div>

        <div className="input-group">
          <label htmlFor="deposit">Deposit Amount</label>
          <input
            id="deposit"
            type="text"
            value={deposit}
            onChange={(e) => setDeposit(e.target.value)}
            placeholder="e.g., 200,000"
          />
        </div>

        <div className="input-group">
          <label htmlFor="interest-rate">Interest Rate (% p.a.)</label>
          <input
            id="interest-rate"
            type="number"
            value={interestRate}
            onChange={(e) => setInterestRate(e.target.value)}
            placeholder="e.g., 11.75"
            step="0.01"
            min="0"
          />
        </div>

        <div className="input-group">
          <label htmlFor="term-years">Repayment Term (Years)</label>
          <select
            id="term-years"
            value={termYears}
            onChange={(e) => setTermYears(e.target.value)}
          >
            {Array.from({ length: 30 }, (_, i) => i + 1).map((year) => (
              <option key={year} value={year}>
                {year} {year === 1 ? "Year" : "Years"}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="calculator-section">
        <h2>Additional Payments</h2>

        <div className="input-group">
          <label htmlFor="additional-monthly">
            Additional Monthly Payment (Optional)
          </label>
          <input
            id="additional-monthly"
            type="text"
            value={additionalMonthlyPayment}
            onChange={(e) => setAdditionalMonthlyPayment(e.target.value)}
            placeholder="e.g., 500"
          />
          <p className="input-hint">
            Any extra amount you pay towards your bond each month
          </p>
        </div>

        <h3>One-Time Lump Sum Payments (Optional)</h3>
        {lumpSumPayments.map((lumpSum, index) => (
          <div key={index} className="lump-sum-row">
            <div className="input-group-small">
              <label htmlFor={`lump-amount-${index}`}>Amount</label>
              <input
                id={`lump-amount-${index}`}
                type="text"
                value={lumpSum.amount || ""}
                onChange={(e) =>
                  handleLumpSumChange(index, "amount", e.target.value)
                }
                placeholder="Amount"
              />
            </div>

            <div className="input-group-small">
              <label htmlFor={`lump-month-${index}`}>Month</label>
              <input
                id={`lump-month-${index}`}
                type="number"
                value={lumpSum.month || ""}
                onChange={(e) =>
                  handleLumpSumChange(index, "month", e.target.value)
                }
                placeholder="Month #"
                min="1"
                max={inputs.termYears * 12}
              />
            </div>

            {lumpSumPayments.length > 1 && (
              <button
                className="remove-button"
                onClick={() => removeLumpSumPayment(index)}
                aria-label="Remove lump sum payment"
              >
                ×
              </button>
            )}
          </div>
        ))}

        <button className="add-button" onClick={addLumpSumPayment}>
          + Add Lump Sum Payment
        </button>

        <h3>Interest Rate Changes (Optional)</h3>
        <p className="input-hint">
          Simulate how your repayments change if interest rates change during
          your bond term
        </p>
        {interestRateChanges.map((rateChange, index) => (
          <div key={index} className="lump-sum-row">
            <div className="input-group-small">
              <label htmlFor={`rate-month-${index}`}>Month</label>
              <input
                id={`rate-month-${index}`}
                type="number"
                value={rateChange.month || ""}
                onChange={(e) =>
                  handleInterestRateChange(index, "month", e.target.value)
                }
                placeholder="Month #"
                min="1"
                max={inputs.termYears * 12}
              />
            </div>

            <div className="input-group-small">
              <label htmlFor={`rate-value-${index}`}>New Rate (% p.a.)</label>
              <input
                id={`rate-value-${index}`}
                type="number"
                value={rateChange.rate || ""}
                onChange={(e) =>
                  handleInterestRateChange(index, "rate", e.target.value)
                }
                placeholder="Rate"
                step="0.01"
                min="0"
              />
            </div>

            {interestRateChanges.length > 1 && (
              <button
                className="remove-button"
                onClick={() => removeInterestRateChange(index)}
                aria-label="Remove interest rate change"
              >
                ×
              </button>
            )}
          </div>
        ))}

        <button className="add-button" onClick={addInterestRateChange}>
          + Add Interest Rate Change
        </button>
      </div>

      {hasValidInputs && (
        <>
          <div className="export-button-container">
            <button className="export-pdf-button" onClick={exportToPDF}>
              📄 Export to PDF
            </button>
          </div>
          <div className="results-section" ref={reportRef}>
            <h2>Repayment Summary</h2>

            <div className="result-grid">
              <div className="result-card">
                <h3>Principal Amount</h3>
                <p className="result-value">
                  {formatCurrency(result.principal)}
                </p>
                <p className="result-detail">
                  {((result.principal / inputs.bondAmount) * 100).toFixed(1)}%
                  of bond amount
                </p>
              </div>

              <div className="result-card">
                <h3>Monthly Payment</h3>
                <p className="result-value">
                  {formatCurrency(result.monthlyPayment)}
                </p>
                <p className="result-detail">
                  Per month for {inputs.termYears} years
                </p>
              </div>

              {inputs.additionalMonthlyPayment > 0 && (
                <div className="result-card highlight">
                  <h3>With Additional Payments</h3>
                  <p className="result-value">
                    {formatCurrency(result.monthlyPaymentWithAdditional)}
                  </p>
                  <p className="result-detail">Per month</p>
                </div>
              )}

              <div className="result-card">
                <h3>Total Interest</h3>
                <p className="result-value">
                  {formatCurrency(result.totalInterestPaid)}
                </p>
                <p className="result-detail">Over {inputs.termYears} years</p>
              </div>

              {inputs.additionalMonthlyPayment > 0 ||
              inputs.lumpSumPayments.some((l) => l.amount > 0) ? (
                <>
                  <div className="result-card">
                    <h3>Interest with Additional Payments</h3>
                    <p className="result-value">
                      {formatCurrency(result.totalInterestPaidWithAdditional)}
                    </p>
                    <p className="result-detail">
                      Over {formatMonths(result.payoffMonths)}
                    </p>
                  </div>

                  <div className="result-card savings">
                    <h3>Total Savings</h3>
                    <p className="result-value">
                      {formatCurrency(result.savings)}
                    </p>
                    <p className="result-detail">
                      Saves{" "}
                      {formatMonths(result.totalMonths - result.payoffMonths)}
                    </p>
                  </div>

                  <div className="result-card">
                    <h3>New Payoff Period</h3>
                    <p className="result-value">
                      {formatMonths(result.payoffMonths)}
                    </p>
                    <p className="result-detail">
                      Reduced from {inputs.termYears} years
                    </p>
                  </div>
                </>
              ) : null}
            </div>

            <div className="total-summary">
              <h3>Total Amount Paid</h3>
              <div className="summary-row">
                <span>Without additional payments:</span>
                <strong>{formatCurrency(result.totalAmountPaid)}</strong>
              </div>
              {inputs.additionalMonthlyPayment > 0 ||
              inputs.lumpSumPayments.some((l) => l.amount > 0) ? (
                <div className="summary-row">
                  <span>With additional payments:</span>
                  <strong>
                    {formatCurrency(result.totalAmountPaidWithAdditional)}
                  </strong>
                </div>
              ) : null}
            </div>

            {/* Repayment Trajectory Chart */}
            {result.monthByMonthData && result.monthByMonthData.length > 0 && (
              <div className="chart-section">
                <h3>Repayment Trajectory</h3>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={result.monthByMonthData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        label={{
                          value: "Month",
                          position: "insideBottom",
                          offset: -10,
                        }}
                      />
                      <YAxis
                        width={56}
                        label={{
                          value: "Balance (R)",
                          angle: -90,
                          position: "insideLeft",
                        }}
                        tickFormatter={formatChartAxisCurrency}
                      />
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        labelFormatter={(label) => `Month ${label}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="standardBalance"
                        stroke="#0279e9"
                        strokeWidth={2}
                        name="Standard Payments"
                        dot={false}
                      />
                      {inputs.additionalMonthlyPayment > 0 && (
                        <Line
                          type="monotone"
                          dataKey="additionalMonthlyBalance"
                          stroke="#28a745"
                          strokeWidth={2}
                          name="With Additional Monthly"
                          dot={false}
                        />
                      )}
                      {inputs.lumpSumPayments.some((l) => l.amount > 0) && (
                        <Line
                          type="monotone"
                          dataKey="lumpSumBalance"
                          stroke="#ff9800"
                          strokeWidth={2}
                          name="With Lump Sums"
                          dot={false}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div className="calculator-save-row">
        <button
          type="button"
          className="save-button"
          onClick={handleSaveClick}
          disabled={!hasValidInputs || saveStatus === "saving"}
        >
          {saveStatus === "saving"
            ? "Saving…"
            : saveStatus === "saved"
              ? "Saved"
              : saveStatus === "error"
                ? "Try again"
                : "Save for later"}
        </button>
      </div>
    </>
  );
};

export default BondRepayment;
